import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { useAuth } from '../../../../hooks/useAuth';
import { useCart } from '../../../../hooks/useCart';
import { useMealPlanner } from '../../../../hooks/useMealPlanner';

import './plan-results.scss';

import { Currency } from '../../../../utils/currency/Currency';
import { MACRO_DEFS, scaleMacro } from '../../../../utils/macros';
import { Button } from '../../../common/button/Button';
import { FrozenDisclaimer } from '../../../common/frozen-disclaimer/FrozenDisclaimer';
import { EditMealModal } from './EditMealModal';
import { PlannedMealCard } from './PlannedMealCard';

const planTotalPrice = (plan) =>
  (plan.days ?? []).reduce(
    (sum, day) =>
      sum +
      (day.meals ?? []).reduce(
        (mealSum, meal) =>
          mealSum +
          (meal.selections ?? []).reduce((s, sel) => s + (sel.item?.price ?? 0) * (sel.grams ?? 0), 0),
        0
      ),
    0
  );

const flattenSelections = (plan) =>
  (plan.days ?? []).flatMap((day) =>
    (day.meals ?? []).flatMap((meal) =>
      (meal.selections ?? []).map((sel) => ({
        date: day.date,
        slotName: meal.slotName,
        fieldId: sel.field?.id,
        fieldName: sel.field?.name,
        itemId: sel.item?.id,
        itemName: sel.item?.name,
        grams: sel.grams
      }))
    )
  );

export const PlanResults = ({ plan, onPlanChange, regeneratePlan, updateSelections, onClose }) => {
  const { user } = useAuth();
  const { addToCart } = useCart();
  const { savePlan } = useMealPlanner();
  const navigate = useNavigate();

  const isLoggedIn = user && !user.isAnonymous;

  const [activeDayId, setActiveDayId] = useState(plan.days?.[0]?.id);
  const [editingMeal, setEditingMeal] = useState(null);
  const [regeneratingKey, setRegeneratingKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(plan.status === 'SAVED');
  const [added, setAdded] = useState(false);

  const activeDay = useMemo(
    () => (plan.days ?? []).find((d) => d.id === activeDayId) ?? plan.days?.[0],
    [plan.days, activeDayId]
  );

  const totalPrice = useMemo(() => planTotalPrice(plan), [plan]);

  const runRegenerate = (request, key) => {
    setRegeneratingKey(key);
    regeneratePlan(request)
      .then(onPlanChange)
      .finally(() => setRegeneratingKey(null));
  };

  const handleRegeneratePlan = () => runRegenerate({ planId: plan.id, scope: 'PLAN' }, 'plan');
  const handleRegenerateDay = () => runRegenerate({ planId: plan.id, scope: 'DAY', dayId: activeDay.id }, `day-${activeDay.id}`);
  const handleRegenerateMeal = (meal) => runRegenerate({ planId: plan.id, scope: 'MEAL', mealId: meal.id }, `meal-${meal.id}`);

  const handleMealSaved = (updatedMeal) => {
    onPlanChange({
      ...plan,
      days: plan.days.map((day) =>
        day.id !== activeDay.id
          ? day
          : { ...day, meals: day.meals.map((m) => (m.id === updatedMeal.id ? updatedMeal : m)) }
      )
    });
  };

  const handleSavePlan = () => {
    setSaving(true);
    savePlan(plan.id)
      .then((updated) => {
        onPlanChange(updated);
        setSaved(true);
      })
      .finally(() => setSaving(false));
  };

  const handleAddToCart = () => {
    addToCart({
      id: uuidv4().toString(),
      name: 'Meal Plan',
      description: `${plan.startDate} to ${plan.endDate}`,
      imageUrl: '/images/menu-items/generic.png',
      price: totalPrice,
      quantity: 1,
      type: 'meal-plan',
      meals: flattenSelections(plan)
    });
    setAdded(true);
    setTimeout(() => onClose(), 1200);
  };

  const handleLogin = () => {
    onClose();
    navigate('/login');
  };

  const dailyMacros = useMemo(() => {
    if (!activeDay) return { calories: 0, protein: 0, carbs: 0, fat: 0 };
    return activeDay.meals.reduce(
      (acc, meal) => {
        (meal.selections ?? []).forEach((sel) => {
          acc.calories += scaleMacro(sel.item?.calories, sel.grams) ?? 0;
          acc.protein += scaleMacro(sel.item?.protein, sel.grams) ?? 0;
          acc.carbs += scaleMacro(sel.item?.carbs, sel.grams) ?? 0;
          acc.fat += scaleMacro(sel.item?.fat, sel.grams) ?? 0;
        });
        return acc;
      },
      { calories: 0, protein: 0, carbs: 0, fat: 0 }
    );
  }, [activeDay]);

  if (!activeDay) return null;

  return (
    <div className="plan-results">
      <div className="plan-results__header">
        <h1 className="plan-results__title">Your Meal Plan</h1>
        <div className="plan-results__sub">
          {plan.startDate} &rarr; {plan.endDate} &middot; {plan.days.length} days
        </div>
      </div>

      <div className="plan-results__body">
        <div className="plan-results__day-tabs">
          {plan.days.map((day) => (
            <button
              key={day.id}
              type="button"
              className={`plan-results__day-tab${day.id === activeDay.id ? ' active' : ''}`}
              onClick={() => setActiveDayId(day.id)}
            >
              {day.date}
            </button>
          ))}
        </div>

        <div className="plan-results__day-macros">
          <div className="plan-results__day-macro-stats">
            {MACRO_DEFS.map(({ key, label, color }) => (
              <div key={key} className="planned-meal-card__macro">
                <span className="planned-meal-card__macro-value" style={{ color }}>
                  {Math.round(dailyMacros[key])}
                  {label}
                </span>
                <span className="planned-meal-card__macro-label">{key}</span>
              </div>
            ))}
          </div>
          <Button secondary small onClick={handleRegenerateDay} loading={regeneratingKey === `day-${activeDay.id}`}>
            Regenerate this day
          </Button>
        </div>

        <div className="plan-results__meals">
          {activeDay.meals.map((meal) => (
            <PlannedMealCard
              key={meal.id}
              meal={meal}
              onEdit={setEditingMeal}
              onRegenerate={handleRegenerateMeal}
              regenerating={regeneratingKey === `meal-${meal.id}`}
            />
          ))}
        </div>

        <FrozenDisclaimer />
      </div>

      <div className="plan-results__footer">
        <Button secondary large onClick={handleRegeneratePlan} loading={regeneratingKey === 'plan'}>
          Regenerate whole plan
        </Button>
        {isLoggedIn ? (
          <Button secondary large onClick={handleSavePlan} loading={saving} disabled={saved}>
            {saved ? 'Plan Saved' : 'Save Plan'}
          </Button>
        ) : (
          <Button secondary large onClick={handleLogin}>Log in to save</Button>
        )}
        <Button large onClick={handleAddToCart} disabled={added}>
          {added ? 'Added to Cart' : <>Add to Cart &middot; <Currency value={totalPrice} /></>}
        </Button>
      </div>

      {editingMeal && (
        <EditMealModal
          open={!!editingMeal}
          onClose={() => setEditingMeal(null)}
          meal={editingMeal}
          planId={plan.id}
          updateSelections={updateSelections}
          onSaved={handleMealSaved}
        />
      )}
    </div>
  );
};
